import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {compose} from "recompose";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import ActionCreator from '../../store/actions/index';
import {withRouter} from "react-router-dom";
import {
    withStyles, Dialog, DialogContent, DialogTitle, DialogActions,
    Table, TableContainer, TableBody, TableRow, TableCell
} from "@material-ui/core";
import t from "../../languages/pl";
import CancelAcceptForm from "./CancelAcceptForm";
import CustomCheckbox from "./CustomCheckbox";
import {themeTypeToBool, notArrayOrEmpty} from '../../utils/helpers';
import NoData from './NoDataRow';
import AssignCarsTemplate from "../../templates/AssignCarsTemplate.csv";
import ResetFilters from '../Filters/ResetFilters';
import TableHeader from './TableHeader';
import guid from './../../utils/guid';
import UploadFileOrTemplate from './UploadFileOrTemplate';
import AdditionalInformationDialog from "./AdditionalInformationDialog";
import SimpleCarsFilter from "../Filters/SimpleCarsFilter";

const useStyles = theme => ({
    container: {
        margin: theme.spacing(2),
        marginTop: theme.spacing(1),
        padding: theme.spacing(1)
    },
    tableContainer: {
        maxHeight: "55vh"
    },
    cell: {
        padding: theme.spacing(1)
    },
    filters: {
        display: "flex",
        justifyContent: "center",
        paddingBottom: theme.spacing(1)
    },
    csvReader: {
        display: "none"
    },
    title: {
        paddingBottom: 0
    },
    buttonLabel: {
        color: "#FFF"
    }
});

const changeCarSelection = (id, state, availableElements) => {
    return availableElements.map(car => {
        if (car.auto_id === id) {
            car.selected = state;
            return car;
        }
        return car;
    });
};

const changeCollectionCarsState = (state, collectionToChange, availableElements) => {
    return availableElements.map(element => {
        if (collectionToChange.find(item => item.auto_id === element.auto_id))
            element.selected = state;
        return element;
    });
};

const AssignCarsDialog = props => {
    const {
        classes, theme, open, onClose, onSubmit, title,
        availableCars, carsInGroup, carIdArray, hideImportExportButton
    } = props;

    const [cars, setCars] = useState([]);
    const [visibleCars, setVisibleCars] = useState([]);
    const [toggleAllStatus, setToggleAllStatus] = useState(false);
    const [incorrectFile, setIncorrectFile] = useState(false);

    useEffect(() => {
        let carsList = availableCars.filter(availableCar => {
            availableCar.selected = !!carsInGroup.find(car => car.auto_id === availableCar.auto_id);

            return availableCar;
        });

        setCars(carsList);
        setVisibleCars(carsList);
    }, [availableCars, carsInGroup]);

    useEffect(() => {
        if (carIdArray) {
            let expandedCars = availableCars.filter(availableCar => {
                availableCar.selected = !!carIdArray.find(carId => carId === availableCar.auto_id);

                return availableCar;
            });

            setCars(expandedCars);
        }
    }, [carIdArray]);


    const handleSubmit = () => {
        let carsToAssign = cars.filter(car => car.selected);
        let carIds = carsToAssign.map(car => car.auto_id);
        if (onSubmit)
            onSubmit(carIds);
    };

    const handleToggle = (id, e) => {
        setCars(changeCarSelection(id, e.target.checked, cars));
    };

    const handleToggleAll = (e) => {
        let newCars = changeCollectionCarsState(e.target.checked, visibleCars, cars);
        setCars(newCars);
        setToggleAllStatus(e.target.checked);
    };

    const handleCsvLoaded = (data) => {
        if (notArrayOrEmpty(data))
            return;
        let registrationNos = [];
        if (data[0].Rejestracja) {
            registrationNos = data.map(registrationNo => {
                if (registrationNo.Rejestracja.length > 0)
                    return registrationNo.Rejestracja.trim();
            });
            let carsToSelect = availableCars.filter(car => {
                if (registrationNos.includes(car.rejestracja))
                    car.selected = true;
                return car;
            });
            setCars(carsToSelect);
        } else {
            setIncorrectFile(true);
        }
    };

    const handleChangeVisibleCars = (carsList) => {
        setVisibleCars(carsList);
    };

    const printList = data => {
        if (!Array.isArray(data) || data.length === 0)
            return <NoData colSpan={3}/>;
        return data.map(row => {
            return (
                <TableRow key={row.auto_id}>
                    <TableCell className={classes.cell}>
                        <CustomCheckbox
                            style={{color: themeTypeToBool(theme.palette.type) ? "#6B6E77" : "#FAFAFA"}}
                            checked={row.selected}
                            onChange={handleToggle.bind(this, row.auto_id)}
                        />
                    </TableCell>
                    <TableCell className={classes.cell}>
                        {row.rejestracja}
                    </TableCell>
                    <TableCell className={classes.cell}>
                        {row.opis}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle className={classes.title}>
                {t.assignCars}&nbsp;{title}
            </DialogTitle>
            <DialogContent className={classes.container}>
                <div>
                    {props.children}
                </div>
                <div className={classes.filters}>
                    <SimpleCarsFilter filteredCarsList={availableCars} onChangeFilter={handleChangeVisibleCars}/>
                </div>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                        <TableHeader
                            data={[
                                <CustomCheckbox
                                    key={guid()}
                                    style={{color: themeTypeToBool(theme.palette.type) ? "#6B6E77" : "#FAFAFA"}}
                                    checked={toggleAllStatus}
                                    onChange={handleToggleAll.bind(this)}
                                />,
                                t.registrationNo,
                                t.description
                            ]}
                            align="left"
                        />
                        <TableBody>
                            {printList(visibleCars)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                {hideImportExportButton
                    ? null
                    :
                    <UploadFileOrTemplate
                        template={AssignCarsTemplate}
                        onFileLoaded={handleCsvLoaded}
                        tooltipText={t.importCsvDescriptionCar}
                        informationDialog
                    />
                }
                <CancelAcceptForm
                    onCancel={onClose}
                    onSubmit={handleSubmit}
                />
            </DialogActions>
            <AdditionalInformationDialog
                open={incorrectFile}
                onClose={() => setIncorrectFile(false)}
                action={t.invalidFileUploaded}
            />
        </Dialog>
    );
};

const mapStateToProps = (state) => ({
    availableCars: state.carsReducer.cars,
    groupFilter: state.filterReducer.group,
    brandFilter: state.filterReducer.brand,
    modelFilter: state.filterReducer.model,
    fuelTypeFilter: state.filterReducer.fuelType,
    gearboxFilter: state.filterReducer.gearbox
});

const mapDispatchToProps = dispatch => {
    return bindActionCreators(ActionCreator, dispatch);
};

AssignCarsDialog.propTypes = {
    classes: PropTypes.object,
    theme: PropTypes.object,
    open: PropTypes.bool,
    title: PropTypes.string,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    children: PropTypes.node,
    carsInGroup: PropTypes.array,
    carIdArray: PropTypes.array,
    hideImportExportButton: PropTypes.bool,
    //redux
    availableCars: PropTypes.array,
    changeAvailableCarSelection: PropTypes.func,
    groupFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    brandFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    modelFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fuelTypeFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    gearboxFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    resetToDefaults: PropTypes.func,
    notifyError: PropTypes.func
};

AssignCarsDialog.defaultProps = {
    carsInGroup: []
};

export default compose(
    withStyles(useStyles, {withTheme: true}),
    withRouter,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(AssignCarsDialog);
